import { useState, useRef, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { createPortal } from 'react-dom'

const OFFSET = 8

// ── Singleton registry ────────────────────────────────────────────────────────
// Only one tooltip may be visible at a time. Each instance subscribes; when any
// tooltip becomes active it broadcasts its ID so all others close themselves.
let _activeId = 0
let _idCounter = 0
const _listeners = new Set()

function _setActive(id) {
  _activeId = id
  _listeners.forEach(fn => fn(id))
}

function _subscribe(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

// ── Popup ─────────────────────────────────────────────────────────────────────
function TooltipPopup({ rect, placement, content }) {
  const style = { position: 'fixed', zIndex: 9999, maxWidth: 260, pointerEvents: 'none' }

  if (placement === 'top') {
    style.left = rect.left + rect.width / 2
    style.top = rect.top - OFFSET
    style.transform = 'translate(-50%, -100%)'
  } else if (placement === 'bottom') {
    style.left = rect.left + rect.width / 2
    style.top = rect.bottom + OFFSET
    style.transform = 'translate(-50%, 0)'
  } else if (placement === 'right') {
    style.left = rect.right + OFFSET
    style.top = rect.top + rect.height / 2
    style.transform = 'translate(0, -50%)'
  } else {
    style.right = window.innerWidth - rect.left + OFFSET
    style.top = rect.top + rect.height / 2
    style.transform = 'translate(0, -50%)'
  }

  return (
    <motion.div
      style={style}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.1 }}
    >
      <div
        className="rounded-lg px-3 py-2 text-xs text-slate-200 leading-snug shadow-2xl whitespace-normal"
        style={{
          background: 'rgba(6, 12, 36, 0.97)',
          border: '1px solid rgba(0, 212, 255, 0.28)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 0 20px rgba(0,212,255,0.08), 0 8px 32px rgba(0,0,0,0.6)',
        }}
      >
        {content}
      </div>
    </motion.div>
  )
}

/**
 * Tooltip wrapper — renders children normally, shows a styled tooltip on hover.
 * Singleton: only one tooltip is visible at a time across the entire app.
 *
 * Accessibility: reveals on focus as well as hover, so keyboard users get the
 * same affordance as mouse users (a pure win for every usage). When the trigger
 * is *itself* the only interactive element — e.g. a bare info ⓘ icon whose sole
 * job is to surface this text — pass `asButton`: the wrapper becomes a focusable
 * `role="button"` with an `aria-label`, toggles on Enter/Space, dismisses on
 * Escape, and is reachable by tap on touch (no hover there). Without `asButton`
 * the wrapper stays a passive span (correct when wrapping a real <button>, so we
 * don't nest interactive roles or add a redundant tab stop).
 *
 * Props:
 *   content    — string or JSX shown in the tooltip
 *   placement  — 'top' | 'bottom' | 'left' | 'right'  (default: 'top')
 *   delay      — ms before tooltip appears (default: 120)
 *   className  — extra classes on the inline wrapper span
 *   asButton   — make the trigger a focusable/tappable button (see above)
 *   label      — accessible name when asButton; falls back to string `content`
 */
export default function Tooltip({ content, children, placement = 'top', delay = 120, className = '', asButton = false, label }) {
  const id = useRef(++_idCounter)
  const [rect, setRect] = useState(null)
  const triggerRef = useRef(null)
  const timerRef = useRef(null)

  // Subscribe to singleton broadcasts — hide this tooltip when another takes over
  useEffect(() => {
    return _subscribe(activeId => {
      if (activeId !== id.current) {
        clearTimeout(timerRef.current)
        setRect(null)
      }
    })
  }, [])

  const show = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        _setActive(id.current)
        setRect(triggerRef.current.getBoundingClientRect())
      }
    }, delay)
  }, [delay])

  const hide = useCallback(() => {
    clearTimeout(timerRef.current)
    _setActive(0)
    setRect(null)
  }, [])

  // Keyboard control for the asButton trigger. Enter/Space toggles (tap focuses
  // the element, which reveals via onFocus); Escape dismisses.
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      rect ? hide() : show()
    } else if (e.key === 'Escape') {
      hide()
    }
  }, [rect, show, hide])

  if (!content) return children

  const a11yProps = asButton
    ? {
        tabIndex: 0,
        role: 'button',
        'aria-label': label ?? (typeof content === 'string' ? content : undefined),
        onFocus: show,
        onBlur: hide,
        onKeyDown,
      }
    : { onFocus: show, onBlur: hide }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        className={`inline-flex items-center ${asButton ? 'rounded outline-none focus-visible:ring-2 focus-visible:ring-accent/60' : ''} ${className}`}
        {...a11yProps}
      >
        {children}
      </span>
      {createPortal(
        <AnimatePresence>
          {rect && <TooltipPopup rect={rect} placement={placement} content={content} />}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}
